import { Loader2 } from 'lucide-react'
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'

import { btnApprove, btnGhost, btnPrimary, btnReject } from './index'
import {
  btnGhostBorderless,
  buttonClass,
  disabledTitle,
  iconButtonClass,
  type ButtonSize,
  type ButtonVariant,
  type IconButtonVariant,
} from './kit'

type NativeButton = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> & { className?: string }

// Read inside the render, not at module scope: index.tsx re-exports this file, so its constants are not yet initialised while this module loads.
function baseFor(variant: ButtonVariant): string {
  switch (variant) {
    case 'primary':
      return btnPrimary
    case 'approve':
      return btnApprove
    case 'danger':
      return btnReject
    case 'ghost':
      return btnGhostBorderless
    default:
      return btnGhost
  }
}

/** One primary per view: the action the page exists for. Everything else is secondary or ghost. */
export const Button = forwardRef<
  HTMLButtonElement,
  NativeButton & {
    variant?: ButtonVariant
    size?: ButtonSize
    loading?: boolean
    icon?: ReactNode
    disabledReason?: string
  }
>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    loading = false,
    icon,
    type = 'button',
    disabled,
    disabledReason,
    title,
    className = '',
    children,
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      title={disabledTitle(!!disabled, disabledReason, title)}
      className={`${buttonClass(baseFor(variant), size)} ${className}`}
      {...rest}
    >
      {loading ? (
        <Loader2 className="h-4 w-4 shrink-0 animate-spin motion-reduce:animate-none" aria-hidden />
      ) : (
        icon
      )}
      {children}
    </button>
  )
})

/** An icon-only button: `label` is its accessible name and its tooltip, so it can never ship unnamed. */
export const IconButton = forwardRef<
  HTMLButtonElement,
  Omit<NativeButton, 'children' | 'aria-label'> & {
    label: string
    icon: ReactNode
    variant?: IconButtonVariant
    size?: ButtonSize
    disabledReason?: string
  }
>(function IconButton(
  { label, icon, variant = 'ghost', size = 'md', type = 'button', title, disabled, disabledReason, className = '', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      disabled={disabled}
      title={disabledTitle(!!disabled, disabledReason, title ?? label)}
      className={`${iconButtonClass(variant, size)} ${className}`}
      {...rest}
    >
      {icon}
    </button>
  )
})
